import React from 'react';

import { plural, useAsyncWrapper } from '@neptune/shared/common-util';
import {
  FilterResult,
  ScopedQueryFilterSearch,
  SEARCH_MODES,
  SearchMode,
} from '@neptune/shared/search-domain';

import { useDebounce } from 'common/hooks/useDebounce';
import { AttributeDefinition } from 'domain/experiment/attribute';

import { highlightAttributeSearchLabels } from './highlight-attribute-search-labels';

export function useSearchAttributes<T extends AttributeDefinition>(
  query: string,
  dataSource: ScopedQueryFilterSearch<T>,
): FilterResult<T & { full?: string }> & {
  error?: any;
  message: string;
  loading?: boolean;
  enterFullSearchMode: () => void;
} {
  const queryDebounced = useDebounce(query, 300);
  const [searchMode, setSearchMode] = React.useState<SearchMode | undefined>(undefined);

  const dataSourceWithHighlight = React.useCallback(
    async (search: string, mode?: SearchMode) => {
      const attributesResult = await dataSource(search, mode);

      const full = highlightAttributeSearchLabels(
        attributesResult.entries.map(({ original }) => original.name),
      );

      const entries = attributesResult.entries.map((entry, i) => ({
        ...entry,
        original: {
          ...entry.original,
          full: full[i],
        },
      }));

      return {
        entries,
        searchMode: attributesResult.searchMode,
        thereIsMore: attributesResult.thereIsMore,
        needsFullSearch: attributesResult.needsFullSearch,
      };
    },
    [dataSource],
  );

  const resolver = React.useCallback(
    async (mode?: SearchMode) => {
      return dataSourceWithHighlight(queryDebounced, mode);
    },
    [dataSourceWithHighlight, queryDebounced],
  );

  const { resolve, result, ...metadata } = useAsyncWrapper({
    resolver,
  });

  const enterFullSearchMode = React.useCallback(() => {
    setSearchMode(SEARCH_MODES.FULL);
    resolve(SEARCH_MODES.FULL);
  }, [resolve]);

  React.useEffect(() => {
    setSearchMode(undefined);
    resolve(undefined);
  }, [queryDebounced, resolve]);

  React.useEffect(() => {
    if (metadata.error) {
      return;
    }

    if (metadata.loading) {
      return;
    }

    if (!result?.needsFullSearch) {
      return;
    }

    if (result?.entries.length > 0) {
      return;
    }

    enterFullSearchMode();
  }, [
    metadata.loading,
    metadata.error,
    enterFullSearchMode,
    result?.needsFullSearch,
    result?.entries.length,
  ]);

  const loading = metadata.loading;
  const attributes = result?.entries ?? [];

  const message = React.useMemo(() => {
    const resultsLabel = plural(attributes.length, { one: 'result', other: 'results' });

    if (loading) {
      if (searchMode === SEARCH_MODES.FULL) {
        return 'Searching across all runs. This can take a few seconds...';
      }

      return 'Searching...';
    }

    if (metadata.error) {
      return "Couldn't load attributes. Please try again.";
    }

    if (attributes.length === 0) {
      if (result?.needsFullSearch) {
        return `No matching results. Please try a full search mode to browse all available metrics.`;
      }

      return 'No matching results found.';
    }

    if (searchMode !== SEARCH_MODES.FULL) {
      return `Showing ${attributes.length} ${resultsLabel} from recently used runs.`;
    }

    return `Showing ${attributes.length} ${resultsLabel}:`;
  }, [attributes.length, searchMode, loading, metadata.error, result?.needsFullSearch]);

  return {
    ...metadata,
    searchMode,
    entries: result?.entries ?? [],
    thereIsMore: result?.thereIsMore ?? false,
    enterFullSearchMode,
    needsFullSearch: (searchMode !== SEARCH_MODES.FULL && result?.needsFullSearch) ?? false,
    message,
  };
}
